import React, { useRef } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { isMainSearchTypeKeywords } from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { getSearchPageResourceLocatorStringParams } from '../../containers/SearchPage/SearchPage.shared';

import { Form, LocationAutocompleteInput } from '../../components';

import css from './LandingSearch.module.css';

const identity = v => v;

const KeywordSearchField = props => {
  const { intl, inputRef } = props;
  return (
    <div className={css.searchInputWrapper}>
      <Field
        name="keywords"
        render={({ input, meta }) => {
          return (
            <input
              className={css.searchInput}
              {...input}
              id="landing-keyword-search"
              data-testid="landing-keyword-search"
              ref={inputRef}
              type="text"
              placeholder={intl.formatMessage({
                id: 'LandingSearch.keywordPlaceholder',
              })}
              autoComplete="off"
            />
          );
        }}
      />
    </div>
  );
};

const LocationSearchField = props => {
  const { intl, inputRef, onLocationChange } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restOfInput } = input;
        const searchInput = { ...restOfInput, onChange: onLocationChange };
        return (
          <LocationAutocompleteInput
            className={css.searchInput}
            iconClassName={css.searchIcon}
            inputClassName={css.locationInput}
            predictionsClassName={css.predictions}
            predictionsAttributionClassName={css.predictionsAttribution}
            placeholder={intl.formatMessage({
              id: 'LandingSearch.locationPlaceholder',
            })}
            closeOnBlur={false}
            inputRef={inputRef}
            input={searchInput}
            meta={meta}
          />
        );
      }}
    />
  );
};

const LandingSearch = props => {
  const { className, rootClassName } = props;
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const searchInputRef = useRef(null);

  const classes = classNames(rootClassName || css.root, className);

  // Get current search params from URL
  const { keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const handleSubmit = values => {
    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // Location search
      const { search, selectedPlace } = values?.location || {};
      if (!selectedPlace) return {};

      const { origin: placeOrigin, bounds: placeBounds } = selectedPlace;
      const originMaybe = config.search?.isOriginInUse ? { origin: placeOrigin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds: placeBounds,
      };
    };

    const searchParams = {
      ...topbarSearchParams(),
    };

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    const path = createResourceLocatorString(
      routeName,
      routeConfiguration,
      pathParams,
      searchParams
    );
    history.push(path);
  };

  const onLocationChange = location => {
    if (!isMainSearchTypeKeywords(config) && location.selectedPlace) {
      // Auto-submit when a place is selected
      handleSubmit({ location });
      // Blur search input to hide software keyboard
      searchInputRef?.current?.blur();
    }
  };

  const onKeywordSubmit = values => {
    if (isMainSearchTypeKeywords(config)) {
      handleSubmit({ keywords: values.keywords });
      // Blur search input to hide software keyboard
      searchInputRef?.current?.blur();
    }
  };

  const isKeywordsSearch = isMainSearchTypeKeywords(config);
  const submit = isKeywordsSearch ? onKeywordSubmit : handleSubmit;

  const initialValues = isKeywordsSearch
    ? { keywords }
    : {
        location:
          address && bounds
            ? {
                search: address,
                selectedPlace: { bounds, ...(origin ? { origin } : {}) },
              }
            : null,
      };

  return (
    <div className={classes}>
      <div className={css.content}>
        <h2 className={css.visuallyHidden}>
          <FormattedMessage id="LandingSearch.title" />
        </h2>
        <FinalForm
          onSubmit={submit}
          initialValues={initialValues}
          render={formRenderProps => {
            const {
              rootClassName,
              className,
              handleSubmit,
              hasSubmitErrors,
              ...restOfProps
            } = formRenderProps;

            const formClasses = classNames(css.form);

            return (
              <Form className={formClasses} onSubmit={handleSubmit}>
                <div className={css.searchContainer}>
                  {isKeywordsSearch ? (
                    <KeywordSearchField intl={intl} inputRef={searchInputRef} />
                  ) : (
                    <LocationSearchField
                      intl={intl}
                      inputRef={searchInputRef}
                      onLocationChange={onLocationChange}
                    />
                  )}
                  <button type="submit" className={css.searchButton}>
                    <FormattedMessage id="LandingSearch.searchButton" />
                  </button>
                </div>
              </Form>
            );
          }}
        />
      </div>
    </div>
  );
};

export default LandingSearch;
